import React, { useState, lazy } from 'react';
import { motion } from 'framer-motion';
type Sponsor = {
  name: string;
  src: string;
};
const sponsors: Sponsor[] = [{
  name: 'Frisby',
  src: 'https://cdn.magicpatterns.com/uploads/pyRB8PXDdWju9G8PoPcMKL/1.svg'
}, {
  name: 'Olímpica Stereo',
  src: 'https://cdn.magicpatterns.com/uploads/mJHKFKtgoTYvTwPvXX2cwY/2.svg'
}, {
  name: 'Chef Burger',
  src: 'https://cdn.magicpatterns.com/uploads/foZdEDgyXUsEVPLmTNehh9/3.svg'
}, {
  name: 'Sandwich Qbano',
  src: 'https://cdn.magicpatterns.com/uploads/uarGBHoAY7Fzhn9HUgNTdt/4.svg'
}, {
  name: 'Radio Tiempo',
  src: 'https://cdn.magicpatterns.com/uploads/ctF2PMdcANdraNLNYtDhyQ/5.svg'
}, {
  name: 'HidraT40',
  src: 'https://cdn.magicpatterns.com/uploads/uGUwLY6y4iWTQeYny6cq4B/6.svg'
}, {
  name: 'RCN Radio',
  src: 'https://cdn.magicpatterns.com/uploads/fDzyVrxv4AcynTNUE8boTE/7.svg'
}, {
  name: 'Colanta',
  src: 'https://cdn.magicpatterns.com/uploads/uKmc9SfJ34pvJNHPeaCNEr/8.svg'
}, {
  name: 'Autozen & Chery',
  src: 'https://cdn.magicpatterns.com/uploads/vL1dCQuQrz8qXgpGPVRur7/9.svg'
}];
// Shared animation config so both layers stay in sync
const MARQUEE_ANIMATE = {
  x: ['0%', '-50%'] as [string, string]
};
const MARQUEE_TRANSITION = {
  duration: 45,
  ease: 'linear' as const,
  repeat: Infinity
};
// Shared track classes so both layers have identical layout
const TRACK_CLASSES = 'flex gap-3 md:gap-4 py-2 md:py-2.5 px-3 md:px-4 w-max';
const TILE_CLASSES = 'relative shrink-0 w-32 h-32 md:w-36 md:h-36';
export function SponsorsBannerSection() {
  const loop = [...sponsors, ...sponsors];
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const isOpen = (i: number) => hoveredIndex === i || activeIndex === i;
  return <section aria-label="Marcas patrocinadoras" className="relative w-full z-20 -mt-12 md:-mt-16 -mb-12 md:-mb-16 pointer-events-none" data-id="element-1043">
      <div className="relative max-w-7xl mx-auto px-4 pointer-events-auto" data-id="element-1044">
        {/* Breathing glow shadow layer (behind the ribbon) */}
        <motion.div aria-hidden="true" className="absolute inset-x-6 inset-y-2 rounded-3xl bg-brand-cyan/40 blur-2xl" animate={{
        opacity: [0.35, 0.6, 0.35],
        scale: [0.98, 1.02, 0.98]
      }} transition={{
        duration: 3.2,
        ease: 'easeInOut',
        repeat: Infinity
      }} data-id="element-1045" />

        {/* Stage holds both layers stacked */}
        <div className="relative" data-id="element-1046">
          {/* LAYER 1 — Visual masked ribbon. Clips marquee and fades edges to transparent. */}
          <div className="relative rounded-3xl bg-white/95 border border-white/80 overflow-hidden" style={{
          boxShadow: '0 2px 4px rgba(15, 23, 42, 0.06), 0 12px 32px -4px rgba(15, 23, 42, 0.12), 0 30px 60px -16px rgba(0, 191, 206, 0.25)',
          WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)',
          maskImage: 'linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)'
        }} data-id="element-1047">
            <motion.div className={TRACK_CLASSES} animate={MARQUEE_ANIMATE} transition={MARQUEE_TRANSITION} data-id="element-1048">
              {loop.map((sponsor, i) => <div key={`v-${sponsor.name}-${i}`} className={`${TILE_CLASSES} rounded-2xl bg-white overflow-hidden transition-transform duration-300 ease-out ${isOpen(i) ? 'scale-110 -translate-y-1 shadow-[0_16px_36px_-8px_rgba(0,191,206,0.45)]' : ''}`} data-id="element-1049">
                  <img src={sponsor.src} alt={sponsor.name} className="w-full h-full object-contain p-3 md:p-4" loading="lazy" data-id="element-1050" />
                </div>)}
            </motion.div>
          </div>

          {/* LAYER 2 — Interactive overlay. Same layout, NO mask, NO overflow clip.
           Holds invisible hitboxes + tooltips so tooltips escape the ribbon freely. */}
          <div className="absolute inset-0 pointer-events-none" style={{
          overflow: 'visible'
        }} data-id="element-1051">
            <motion.div className={TRACK_CLASSES} animate={MARQUEE_ANIMATE} transition={MARQUEE_TRANSITION} data-id="element-1052">
              {loop.map((sponsor, i) => <div key={`i-${sponsor.name}-${i}`} className={`${TILE_CLASSES} pointer-events-auto`} onMouseEnter={() => setHoveredIndex(i)} onMouseLeave={() => setHoveredIndex(prev => prev === i ? null : prev)} data-id="element-1053">
                  <button type="button" onClick={() => setActiveIndex(prev => prev === i ? null : i)} aria-label={`${sponsor.name} — patrocinador`} className="absolute inset-0 cursor-pointer rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-cyan focus-visible:ring-offset-2" data-id="element-1054" />

                  {/* Tooltip — escapes upward freely because overlay has overflow:visible */}
                  <div role="tooltip" className={`pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-4 z-30 transition-all duration-200 ${isOpen(i) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1'}`} data-id="element-1055">
                    <div className="whitespace-nowrap px-3 py-1.5 rounded-full bg-brand-navy text-white text-[11px] md:text-xs font-medium shadow-lg" data-id="element-1056">
                      🐾 ¡Únete tú también!
                    </div>
                    <div className="w-2 h-2 bg-brand-navy rotate-45 absolute -bottom-1 left-1/2 -translate-x-1/2" data-id="element-1057" />
                  </div>
                </div>)}
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}